import { useCallback, useMemo, useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import { Handshake, Plus } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageTitle from "@/components/PageTitle";
import { Button } from "@/components/ui/Button";
import {
  wholesale_forecast_by_account_export_path,
  wholesale_forecast_by_account_path,
  wholesale_forecast_by_account_upsert_path,
  wholesale_customers_path,
} from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import ActionsMenu from "./components/ActionsMenu";
import AddProductDialog from "./components/AddProductDialog";
import ChannelSelect, { type ChannelOption } from "./components/ChannelSelect";
import ForecastGrid from "./components/ForecastGrid";
import ImportDialog from "./components/ImportDialog";
import {
  isProductInList,
  mergeScaffolds,
  type CustomerInfo,
  type ForecastProduct,
  type ScaffoldProduct,
  type ScaffoldRow,
} from "./lib/forecastGrid";

interface Props {
  plan: { id: string; name: string } | null;
  currency: string | null;
  months: string[];
  locked_through: string;
  products: ForecastProduct[];
  customers: CustomerInfo[];
  filtered_product: { external_id: string; name: string | null; short_name: string | null } | null;
  channels: ChannelOption[];
  selected_channel_id: string | null;
  can: { edit: boolean; import: boolean; export: boolean };
}

export default function ForecastByAccountIndex({
  plan,
  currency,
  months,
  locked_through,
  products,
  customers,
  filtered_product,
  channels,
  selected_channel_id,
  can,
}: Props) {
  const market = useMarket();
  const [importOpen, setImportOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [scaffoldProducts, setScaffoldProducts] = useState<ScaffoldProduct[]>([]);
  const [scaffoldRows, setScaffoldRows] = useState<ScaffoldRow[]>([]);
  const currentUserId = (usePage().props as { user?: { id: string } | null }).user?.id ?? null;

  const customersById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);
  const mergedProducts = useMemo(
    () => mergeScaffolds(products, scaffoldProducts, scaffoldRows, customersById),
    [products, scaffoldProducts, scaffoldRows, customersById],
  );
  const existingExternalIds = useMemo(
    () => new Set(mergedProducts.map((p) => p.external_id)),
    [mergedProducts],
  );

  const handleCellSave = useCallback(
    (customerId: string, productExternalId: string, month: string, units: string) => {
      router.put(
        wholesale_forecast_by_account_upsert_path(market.slug),
        {
          wholesale_customer_id: customerId,
          product_external_id: productExternalId,
          month,
          units,
          channel_id: selected_channel_id,
        },
        { preserveScroll: true, preserveState: true, only: ["products"] },
      );
    },
    [market.slug, selected_channel_id],
  );

  const handleAddProduct = useCallback(
    (product: ScaffoldProduct) => {
      setScaffoldProducts((prev) =>
        isProductInList(products, prev, product.external_id) ? prev : [...prev, product],
      );
    },
    [products],
  );

  const handleAddCustomer = useCallback((productExternalId: string, customerId: string) => {
    setScaffoldRows((prev) =>
      prev.some((r) => r.productExternalId === productExternalId && r.customerId === customerId)
        ? prev
        : [...prev, { productExternalId, customerId }],
    );
  }, []);

  const exportHref = wholesale_forecast_by_account_export_path(
    market.slug,
    selected_channel_id ? { channel_id: selected_channel_id } : {},
  );
  const addProductButton = can.edit && (
    <Button variant="outline" size="sm" className="gap-1" onClick={() => setAddProductOpen(true)}>
      <Plus size={14} />
      Add product
    </Button>
  );

  return (
    <AppLayout
      header={
        <PageTitle
          title="Wholesale Forecast"
          subtitle={`${plan ? `${plan.name} · ` : ""}Planned units per product × customer × month. Locked months are read-only.`}
        />
      }
      headerActions={
        // Wholesale Customers has no Menu link; this button is the way in,
        // so it shows even without a plan.
        <div className="flex items-center gap-2">
          {plan && channels.length > 1 && (
            <ChannelSelect marketSlug={market.slug} channels={channels} selectedChannelId={selected_channel_id} />
          )}
          <Button variant="outline" size="sm" className="gap-1" asChild>
            <Link href={wholesale_customers_path(market.slug)}>
              <Handshake size={14} />
              Wholesale Customers
            </Link>
          </Button>
          {plan && addProductButton}
          {plan && (
            <ActionsMenu
              canExport={can.export}
              canImport={can.import}
              exportHref={exportHref}
              onOpenImport={() => setImportOpen(true)}
            />
          )}
        </div>
      }
    >
      {plan && filtered_product && (
        <div className="mb-3 flex items-center gap-2 rounded-md border px-3 py-2 text-xs">
          <span className="text-muted-foreground">Viewing one product:</span>
          <span className="font-semibold text-foreground">
            {filtered_product.name ?? "Unknown product"}
          </span>
          <span className="text-muted-foreground">#{filtered_product.external_id}</span>
          <Link href={wholesale_forecast_by_account_path(market.slug)} className="ml-auto font-medium text-link hover:underline">
            Show all products
          </Link>
        </div>
      )}

      {plan ? (
        <ForecastGrid
          months={months}
          lockedThrough={locked_through}
          products={mergedProducts}
          currency={currency}
          canEdit={can.edit}
          currentUserId={currentUserId}
          customers={customers}
          onCellSave={handleCellSave}
          onAddCustomer={handleAddCustomer}
          onAddProduct={() => setAddProductOpen(true)}
        />
      ) : (
        <div className="rounded-lg border px-4 py-12 text-center text-xs text-muted-foreground">
          No current TopDown plan — wholesale forecasts belong to a plan. Create and publish one under Top Down
          first.
        </div>
      )}

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} channelId={selected_channel_id} />
      <AddProductDialog
        open={addProductOpen}
        existingExternalIds={existingExternalIds}
        onAdd={handleAddProduct}
        onClose={() => setAddProductOpen(false)}
      />
    </AppLayout>
  );
}
